import { ReactComponent as CloseIcon } from "../../assets/icons/closeIcon.svg";
import { ReactComponent as RadioIcon } from "../../assets/icons/radioIcon.svg";
import excelImage from "../../assets/images/excel.png";
import Button from "../../../common/components/button";
import React, { use, useEffect, useRef, useState } from "react";
import { subMonths } from "date-fns";
import DayPickerInput from "./DayPickerInput";
import CheckBox from "./checkBox";
import { useGetCommonDelivery } from "../../../common/hooks/common/useGetCommonDelivery";
import { useGetOrderCustomer } from "../../../common/hooks/console/transaction/useGetOrderCustomer";
import { useGetOrderBranch } from "../../../common/hooks/console/transaction/useGetOrderBranch";
import { usePostOrderExcelDownload } from "../../../common/hooks/console/transaction/usePostOrderExcelDownload";
import { usePostOrderExcelForm } from "../../../common/hooks/console/transaction/usePostOrderExcelForm";
import { usePostOrderReceiptDownload } from "../../../common/hooks/console/transaction/usePostOrderReceiptDownload";
import { ExcelResponse, usePostExcelFile } from "../../../common/hooks/console/transaction/usePostExcelFile";
import useToastStore from "../../store/useToastStore";
import { usePostOtherBranchDownload } from "../../../common/hooks/console/otherBranch/usePostOtherBranchDownload";
import PortalSelectBox from "./portalSelectBox";
import { useAuthStore } from "../../store/useAuthStore";
import { useSystemPopStore } from "console/store/useSystemPopStore";

interface ExcelProps {
    selectedIds?: number[];
    onClose: () => void;
    onlyDownload?: boolean;
    onlyReceipt?: boolean;
    initialDownloadClick?: "excel" | "receipt" | "branch";
    customer?: string;
    customer_idx?: number;
    branch?: string;
    startDate?: Date;
    endDate?: Date;
    setStartDate?: (date: Date | undefined) => void;
    setEndDate?: (date: Date | undefined) => void;
    onDateChange?: (start: Date, end: Date) => void;
    onDateSubmit?: (start: string | undefined, end: string | undefined) => void;
}
type ExcelFormType = {
    order_status: number[];
    payment_status: number[];
    branch_type: number[];
    delivery_name: string[];
    customer_idx: string[];
    customer_branch_idx: string[];
    start_date: string | undefined;
    start_date_time: string;
    end_date: string | undefined;
    end_date_time: string;
    order_idx: number[];
};

const Excel = ({
    onClose,
    onlyDownload,
    setStartDate,
    startDate,
    endDate,
    setEndDate,
    onDateChange,
    selectedIds,
    onlyReceipt,
    customer,
    customer_idx,
    branch,
    onDateSubmit,
    initialDownloadClick,
}: ExcelProps) => {
    const { addToast } = useToastStore();
    const { showPop } = useSystemPopStore();

    const level = useAuthStore(state => state.m_level);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [downloadClick, setDownloadClick] = useState<string>("");
    const [uploadClick, setUploadClick] = useState(false);
    const [selectedOption, setSelectedOption] = useState<string>("");
    const [customerTxt, setCustomerTxt] = useState<string>("");
    const [branchTxt, setBranchTxt] = useState<string>("");
    const [deliveryTxt, setDeliveryTxt] = useState<string>("");
    const [selectedCustomers, setSelectedCustomers] = useState<{ value: string; label: string }[]>([]);
    const [selectedBranches, setSelectedBranches] = useState<{ value: string; label: string }[]>([]);
    const [selectedDelivery, setSelectedDelivery] = useState<{ value: string; label: string }[]>([]);
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "success">("idle");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [result, setResult] = useState<ExcelResponse | null>(null);
    const [failPop, setFailPop] = useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const filterRef = useRef<HTMLDivElement>(null);

    const { data: deliveryData } = useGetCommonDelivery(deliveryTxt);
    const { data: customerData } = useGetOrderCustomer(customerTxt, customer_idx);
    const { data: branchData } = useGetOrderBranch(branchTxt, initialDownloadClick === "branch" ? customer_idx : undefined);
    const { mutate: postExcelFile } = usePostExcelFile(data => {
        setResult(data);
        setUploadStatus("success");
        addToast({
            type: "success",
            message: `${data.success_cnt}건이 등록되었습니다.`,
        });
    });

    const [excelForm, setExcelForm] = useState<ExcelFormType>({
        order_status: [1, 2, 3, 4, 5, 6],
        payment_status: [1, 2],
        branch_type: [1, 2],
        delivery_name: [],
        customer_idx: [],
        customer_branch_idx: [],
        start_date: "",
        start_date_time: "00:00:00",
        end_date: "",
        end_date_time: "23:59:59",
        order_idx: [],
    });

    const orderStatusOptions = [
        { label: "미확인", value: 1 },
        { label: "접수완료", value: 2 },
        { label: "준비완료", value: 3 },
        { label: "배송완료", value: 4 },
        { label: "취소", value: 5 },
        { label: "보류", value: 6 },
    ];
    const paymentStatusOptions = [
        { label: "입금", value: 2 },
        { label: "미수", value: 1 },
    ];

    const branchTypeOptions = [
        { label: "본점", value: 1 },
        { label: "타지점", value: 2 },
    ];
    const [excelIncludeOption, setExcelIncludeOption] = useState<1 | 2>(1); // 1: 포함, 2: 미포함

    const handleCheckboxChange = (field: "order_status" | "payment_status" | "branch_type", value: number) => {
        setExcelForm(prev => ({
            ...prev,
            [field]: prev[field].includes(value)
                ? (prev[field] as number[]).filter(item => item !== value)
                : [...(prev[field] as number[]), value],
        }));
    };

    const dateOptions = [
        { value: "today", label: "오늘", monthsAgo: 0 },
        { value: "month", label: "1개월", monthsAgo: 1 },
        { value: "threeMonths", label: "3개월", monthsAgo: 3 },
    ];

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) {
            setExcelFile(file);
            setUploadStatus("uploading");
            postExcelFile(file, {
                onError: error => {
                    setUploadStatus("idle");
                    setErrorMessage("파일 업로드에 실패했습니다. 다시 시도해주세요.");
                },
            });
        }
    };

    const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setExcelFile(file);
            setUploadStatus("uploading");
            postExcelFile(file, {
                onError: error => {
                    setUploadStatus("idle");
                    setErrorMessage("파일 업로드에 실패했습니다. 다시 시도해주세요.");
                },
            });
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setExcelFile(event.target.files[0]);
        }
    };
    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const handleDateSelection = (value: string, monthsAgo: number) => {
        const today = new Date();
        const calculatedStartDate = monthsAgo === 0 ? today : subMonths(today, monthsAgo);

        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(today.getFullYear() - 1);

        if (calculatedStartDate < oneYearAgo) {
            alert("최대 1년까지만 조회할 수 있습니다.");
            return;
        }

        setSelectedOption(value);
        setStartDate?.(calculatedStartDate);
        setEndDate?.(today);

        setExcelForm(prev => ({
            ...prev,
            start_date: calculatedStartDate.toISOString().split("T")[0],
            end_date: today.toISOString().split("T")[0],
        }));
    };

    const handleCustomerSelect = (value: string, label: string) => {
        setSelectedCustomers(prev => {
            if (prev.some(customer => customer.value === value)) return prev;
            return [...prev, { value, label }];
        });
    };

    const handleRemoveCustomer = (value: string, label: string) => {
        setSelectedCustomers(prev => prev.filter(customer => customer.value !== value));
    };
    const handleBranchSelect = (value: string, label: string) => {
        setSelectedBranches(prev => {
            if (prev.some(branch => branch.value === value)) return prev;
            return [...prev, { value, label }];
        });
    };
    const handleRemoveBranch = (value: string, label: string) => {
        setSelectedBranches(prev => prev.filter(branch => branch.value !== value));
    };
    const handleDeliverySelect = (value: string, label: string) => {
        setSelectedDelivery(prev => {
            if (prev.some(delivery => delivery.value === value)) return prev;
            return [...prev, { value, label }];
        });
    };
    const handleRemoveDelivery = (value: string, label: string) => {
        setSelectedDelivery(prev => prev.filter(delivery => delivery.value !== value));
    };

    const { mutate: downloadExcel } = usePostOrderExcelDownload(undefined, error => {
        const message = error?.response?.data?.message || "조회 기간은 최대 12개월까지만 가능합니다.";
        alert(message);
    });

    const { mutate: downloadReceipt } = usePostOrderReceiptDownload(undefined, error => {
        const message = error?.response?.data?.message || "조회 기간은 최대 12개월까지만 가능합니다.";
        alert(message);
    });
    const { mutate: downloadOtherBranch } = usePostOtherBranchDownload(undefined, error => {
        const message = error?.response?.data?.message || "조회 기간은 최대 12개월까지만 가능합니다.";
        alert(message);
    });
    const { mutate: formExcel } = usePostOrderExcelForm();

    const handleDownload = () => {
        if (downloadClick === "receipt" && initialDownloadClick !== "receipt") {
            showPop({
                title: (selectedCustomers.length > 0 || selectedBranches.length > 0)
                    ? `${[
                        ...selectedCustomers.map(customer => customer.label),
                        ...selectedBranches.map(branch => branch.label)
                    ].join(', ')} 선택한게 맞나요?`
                    : "전체 업체를 선택한게 맞나요?",
                rightButton: "확인",
                type: "positive",
                onRightClick: () => {
                    if (startDate && endDate && onDateSubmit) {
                        const formattedStart = startDate.toISOString().split("T")[0];
                        const formattedEnd = endDate.toISOString().split("T")[0];
                        onDateSubmit(formattedStart, formattedEnd);
                    }

                    const updatedForm = {
                        ...excelForm,
                        customer_idx: selectedCustomers.map(item => item.value),
                        customer_branch_idx: selectedBranches.map(item => item.value),
                        delivery_name: selectedDelivery.map(item => item.label),
                        order_idx: selectedIds || [],
                        menu: window.location.pathname.includes('/console/transaction') ? 'transaction' : 'sales'
                    };
                    downloadReceipt({
                        ...updatedForm,
                        is_excel_date: excelIncludeOption === 1,
                    });
                },
            });
            return;
        }

        if (startDate && endDate && onDateSubmit) {
            const formattedStart = startDate.toISOString().split("T")[0];
            const formattedEnd = endDate.toISOString().split("T")[0];
            onDateSubmit(formattedStart, formattedEnd);
        }

        const updatedForm = {
            ...excelForm,
            customer_idx: selectedCustomers.map(item => item.value),
            customer_branch_idx: selectedBranches.map(item => item.value),
            delivery_name: selectedDelivery.map(item => item.label),
            order_idx: selectedIds || [],
            menu: window.location.pathname.includes('/console/transaction') ? 'transaction' : 'sales'
        };
        if (downloadClick === "excel") downloadExcel(updatedForm);
        if (downloadClick === "receipt") downloadReceipt({
            ...updatedForm,
            is_excel_date: excelIncludeOption === 1,
        });
        if (downloadClick === "branch") downloadOtherBranch(updatedForm);
    };

    const handleStartDateChange = (dateValue: Date | undefined) => {
        if (!dateValue) return;
        const adjustedDate = new Date(dateValue);
        adjustedDate.setMinutes(adjustedDate.getMinutes() - adjustedDate.getTimezoneOffset());
        setExcelForm(prev => ({
            ...prev,
            start_date: adjustedDate.toISOString().split("T")[0],
        }));
        setStartDate?.(adjustedDate);
        setSelectedOption("");
    };

    const handleEndDateChange = (dateValue: Date | undefined) => {
        if (!dateValue) return;
        const adjustedDate = new Date(dateValue);
        adjustedDate.setMinutes(adjustedDate.getMinutes() - adjustedDate.getTimezoneOffset());
        setExcelForm(prev => ({
            ...prev,
            end_date: adjustedDate.toISOString().split("T")[0],
        }));
        setEndDate?.(adjustedDate);
        setSelectedOption("");
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target as Node) &&
                !(event.target as HTMLElement).closest(".selectbox-dropdown")
            ) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    useEffect(() => {
        if (branch && initialDownloadClick === "branch" && branchData) {
            const branchMatch = branchData?.find(b => b.customer_name === branch);
            if (branchMatch) {
                handleBranchSelect(branchMatch.customer_idx.toString(), branchMatch.customer_name);
            }
        }
    }, [branch, initialDownloadClick, branchData]);

    useEffect(() => {
        if (customer && customer_idx) {
            const timeout = setTimeout(() => {
                const customerMatch = customerData?.find(c => c.customer_idx === customer_idx);
                if (customerMatch) {
                    handleCustomerSelect(customerMatch.customer_idx.toString(), customerMatch.customer_name);
                }
            }, 0);

            return () => clearTimeout(timeout);
        }
    }, [customer, customer_idx, customerData]);

    useEffect(() => {
        onlyDownload && setDownloadClick("excel");
    }, [onlyDownload]);

    useEffect(() => {
        initialDownloadClick === "branch" && setDownloadClick("branch");
    }, [initialDownloadClick]);

    useEffect(() => {
        if (startDate) {
            const formattedStart = startDate.toISOString().split("T")[0];
            setExcelForm(prev => ({
                ...prev,
                start_date: formattedStart,
            }));
        }
        if (endDate) {
            const formattedEnd = endDate.toISOString().split("T")[0];
            setExcelForm(prev => ({
                ...prev,
                end_date: formattedEnd,
            }));
        }
    }, [startDate, endDate]);

    return (
        <div ref={filterRef} className="relative">
            {!onlyDownload &&
                (downloadClick === "excel" || downloadClick === "receipt" || downloadClick === "branch" || uploadClick || (
                    <div className="flex flex-col w-40 top-2 rounded-xl p-2 right-0 absolute bg-white items-center text-gray2 shadow-md">
                        <div
                            onClick={() => {
                                setDownloadClick("excel");
                            }}
                            className="h-10 px-5 whitespace-nowrap flex items-center cursor-pointer rounded-lg hover:bg-primary4 hover:text-primary2 w-full justify-center text-[16px]"
                        >
                            주문판매 다운로드
                        </div>
                        <div
                            onClick={() => {
                                setDownloadClick("receipt");
                            }}
                            className="h-10 px-5 whitespace-nowrap flex items-center cursor-pointer rounded-lg hover:bg-primary4 hover:text-primary2 w-full justify-center text-[16px]"
                        >
                            청구서 다운로드
                        </div>
                        {!onlyReceipt && (
                            <>
                                <div
                                    onClick={() => {
                                        setDownloadClick("branch");
                                    }}
                                    className="h-10 px-5 whitespace-nowrap flex items-center cursor-pointer rounded-lg hover:bg-primary4 hover:text-primary2 w-full justify-center text-[16px]"
                                >
                                    타지점발주 다운로드
                                </div>
                                {level !== 7 && (
                                    <div
                                        onClick={() => {
                                            setUploadClick(true);
                                        }}
                                        className="h-10 px-5 flex whitespace-nowrap items-center cursor-pointer rounded-lg hover:bg-primary4 hover:text-primary2 w-full justify-center text-[16px]"
                                    >
                                        주문판매 업로드
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ))}
            {(downloadClick === "excel" || downloadClick === "receipt" || downloadClick === "branch") && (
                <div className="absolute right-0 top-2 w-[680px] bg-white rounded-2xl border shadow-lg">
                    <div className="flex items-center justify-between px-5 py-6">
                        <p className="text-2xl font-bold">
                            {downloadClick === "excel"
                                ? "주문판매 다운로드"
                                : downloadClick === "receipt"
                                  ? "청구서 다운로드"
                                  : "타지점발주 다운로드"}
                        </p>
                        <CloseIcon className="cursor-pointer" onClick={onClose} />
                    </div>
                    <div className="bg-[#F6F7FA] flex flex-col gap-3 p-5">
                        <div className="flex items-start">
                            <p className="w-[100px] text-sm text-gray2">배송일자</p>
                            <div className="w-full flex flex-col gap-2">
                                <div className="grid grid-cols-4 h-[34px]">
                                    {dateOptions.map(option => (
                                        <div
                                            key={option.value}
                                            className={`flex items-center w-full gap-2 cursor-pointer ${
                                                selectedOption === option.value ? "text-primary2" : "text-gray2"
                                            }`}
                                            onClick={() => handleDateSelection(option.value, option.monthsAgo)}
                                        >
                                            {selectedOption === option.value ? (
                                                <RadioIcon className="w-8 h-8 -ml-1.5" />
                                            ) : (
                                                <div className="border-gray4 h-[19px] w-[19px] border-2 rounded-full" />
                                            )}
                                            <span className={`${selectedOption === option.value && "-ml-[7px]"}`}>{option.label}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-3 relative z-50 bg-white h-[34px] px-3 border border-gray4 rounded-lg">
                                    <DayPickerInput
                                        twoCalendar
                                        maxDate={excelForm.end_date ? new Date(excelForm.end_date) : undefined}
                                        initialDate={excelForm.start_date ? new Date(excelForm.start_date) : undefined}
                                        onDateChange={handleStartDateChange}
                                    />
                                    <DayPickerInput
                                        minDate={excelForm.start_date ? new Date(excelForm.start_date) : undefined}
                                        twoCalendar
                                        initialDate={excelForm.end_date ? new Date(excelForm.end_date) : undefined}
                                        onDateChange={handleEndDateChange}
                                    />
                                </div>
                            </div>
                        </div>
                        <hr className="border-gray4" />
                        <div className="flex items-start">
                            <p className="w-[100px] text-sm text-gray2">주문상태</p>
                            <div className="w-full text-base grid grid-cols-4  gap-2">
                                {orderStatusOptions.map(option => (
                                    <CheckBox
                                        key={option.value}
                                        label={option.label}
                                        checked={excelForm.order_status.includes(option.value)}
                                        onChange={() => handleCheckboxChange("order_status", option.value)}
                                    />
                                ))}
                            </div>
                        </div>
                        <hr className="border-gray4" />
                        <div className="flex items-center">
                            <p className="w-[86px] text-sm text-gray2">입금상태</p>
                            <div className="flex gap-[80px]">
                                {paymentStatusOptions.map(option => (
                                    <CheckBox
                                        key={option.value}
                                        label={option.label}
                                        checked={excelForm.payment_status.includes(option.value)}
                                        onChange={() => handleCheckboxChange("payment_status", option.value)}
                                    />
                                ))}
                            </div>
                        </div>
                        <hr className="border-gray4" />
                        <div className="flex items-center">
                            <p className="w-[86px] text-sm text-gray2">발주상태</p>
                            <div className="flex gap-[80px]">
                                {branchTypeOptions.map(option => (
                                    <CheckBox
                                        key={option.value}
                                        label={option.label}
                                        checked={excelForm.branch_type.includes(option.value)}
                                        onChange={() => handleCheckboxChange("branch_type", option.value)}
                                    />
                                ))}
                            </div>
                        </div>
                        {downloadClick === "receipt" && (
                            <>
                                <hr className="border-gray4" />
                                <div className="flex items-center">
                                    <p className="w-[86px] text-sm text-gray2">청구일자</p>
                                    <div className="flex gap-[86px] items-center text-gray2">
                                        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExcelIncludeOption(1)}>
                                            {excelIncludeOption === 1 ? (
                                                <RadioIcon className="w-8 h-8 -mr-[7px] -ml-1.5" />
                                            ) : (
                                                <div className="border-gray4 h-[19px] w-[19px] border-2 rounded-full" />
                                            )}
                                            <span>포함</span>
                                        </div>
                                        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExcelIncludeOption(2)}>
                                            {excelIncludeOption === 2 ? (
                                                <RadioIcon className="w-8 h-8 -mr-[7px] -ml-1.5" />
                                            ) : (
                                                <div className="border-gray4 h-[19px] w-[19px] border-2 rounded-full" />
                                            )}
                                            <span>미포함</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <hr className="border-gray4" />
                        {onlyDownload || (
                            <>
                                {downloadClick === "branch" || (
                                    <div className="flex flex-col">
                                        <div className="flex items-center">
                                            <p className="w-[100px] text-sm text-gray2">고객별</p>
                                            <PortalSelectBox
                                                onSearchChange={value => setCustomerTxt(value)}
                                                onChange={value => {
                                                    const selectedOption = customerData?.find(
                                                        customer => customer.customer_idx.toString() === value,
                                                    );
                                                    if (selectedOption) {
                                                        handleCustomerSelect(value, selectedOption.customer_name);
                                                    }
                                                }}
                                                searchable
                                                options={
                                                    customerData
                                                        ? customerData.map(customer => ({
                                                              value: customer.customer_idx.toString(),
                                                              label: customer.customer_name,
                                                          }))
                                                        : []
                                                }
                                                className="w-full bg-white z-10"
                                                placeholder="미선택시 [전체]로 호출됩니다."
                                                focusPlaceHolder="고객 검색 후 선택시, 복수선택이 가능합니다"
                                                height="max-h-52"
                                            />
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-1 ml-[86px]">
                                            {selectedCustomers.map(customer => (
                                                <div
                                                    key={customer.value}
                                                    className="px-2 py-1 rounded-3xl flex items-center bg-white border border-gray5 gap-1"
                                                >
                                                    <p className="text-sm text-gray2">{customer.label}</p>
                                                    <CloseIcon
                                                        className="w-4 h-4 cursor-pointer"
                                                        onClick={() => handleRemoveCustomer(customer.value, customer.label)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center">
                                        <p className="w-[100px] text-sm text-gray2">타지점별</p>
                                        <PortalSelectBox
                                            onSearchChange={value => setBranchTxt(value)}
                                            onChange={value => {
                                                const selectedOption = branchData?.find(branch => branch.customer_idx.toString() === value);
                                                if (selectedOption) {
                                                    handleBranchSelect(value, selectedOption.customer_name);
                                                }
                                            }}
                                            searchable
                                            options={
                                                branchData
                                                    ? branchData.map(branch => ({
                                                          value: branch.customer_idx.toString(),
                                                          label: branch.customer_name,
                                                      }))
                                                    : []
                                            }
                                            className="w-full bg-white"
                                            placeholder="미선택시 [전체]로 호출됩니다."
                                            focusPlaceHolder="고객 검색 후 선택시, 복수선택이 가능합니다"
                                            height="max-h-36"
                                        />
                                    </div>
                                    <div className="flex flex-wrap gap-1 ml-[86px]">
                                        {selectedBranches.map(branch => (
                                            <div
                                                key={branch.value}
                                                className="px-2 py-1 rounded-3xl flex items-center bg-white border border-gray5 gap-1"
                                            >
                                                <p className="text-sm text-gray2">{branch.label}</p>
                                                <CloseIcon
                                                    className="w-4 h-4 cursor-pointer"
                                                    onClick={() => handleRemoveBranch(branch.value, branch.label)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                        {downloadClick === "branch" || (
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center">
                                    <p className="w-[100px] text-sm text-gray2">배송업체별</p>
                                    <PortalSelectBox
                                        onSearchChange={value => setDeliveryTxt(value)}
                                        onChange={value => {
                                            const selectedOption = deliveryData?.data.find(
                                                delivery => delivery.delivery_idx.toString() === value,
                                            );
                                            if (selectedOption) {
                                                handleDeliverySelect(value, selectedOption.delivery_name);
                                            }
                                        }}
                                        searchable
                                        options={
                                            deliveryData
                                                ? deliveryData.data.map(delivery => ({
                                                      value: delivery.delivery_idx.toString(),
                                                      label: delivery.delivery_name,
                                                  }))
                                                : []
                                        }
                                        className="w-full bg-white"
                                        placeholder="미선택시 [전체]로 호출됩니다."
                                        focusPlaceHolder="고객 검색 후 선택시, 복수선택이 가능합니다"
                                        height="max-h-24"
                                    />
                                </div>
                                <div className="flex flex-wrap gap-1 ml-[86px]">
                                    {selectedDelivery.map(delivery => (
                                        <div
                                            key={delivery.value}
                                            className="px-2 py-1 rounded-3xl flex items-center bg-white border border-gray5 gap-1"
                                        >
                                            <p className="text-sm text-gray2">{delivery.label}</p>
                                            <CloseIcon
                                                className="w-4 h-4 cursor-pointer"
                                                onClick={() => handleRemoveDelivery(delivery.value, delivery.label)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center justify-between px-6 h-[84px] gap-2">
                        <Button
                            size="L"
                            className="w-full"
                            onClick={() => {
                                setExcelForm({
                                    order_status: [1, 2, 3, 4, 5, 6],
                                    payment_status: [1, 2],
                                    branch_type: [1, 2],
                                    delivery_name: [],
                                    customer_idx: [],
                                    customer_branch_idx: [],
                                    start_date: undefined,
                                    start_date_time: "00:00:00",
                                    end_date: undefined,
                                    end_date_time: "23:59:59",
                                    order_idx: selectedIds || [],
                                });
                                setStartDate?.(undefined);
                                setEndDate?.(undefined);
                                setSelectedOption("");
                            }}
                        >
                            초기화
                        </Button>
                        <Button size="L" className="w-[480px] flex-shrink-0" status="regist" onClick={handleDownload}>
                            다운로드
                        </Button>
                    </div>
                </div>
            )}
            {uploadClick && (
                <div className="absolute right-0 top-2 w-[680px] bg-white rounded-2xl border shadow-lg">
                    <div className="flex items-center justify-between px-5 py-6">
                        <p className="text-2xl font-bold">주문판매 업로드</p>
                        <CloseIcon className="cursor-pointer" onClick={onClose} />
                    </div>

                    <div className="flex mx-6 -mb-3 items-center gap-2">
                        <Button size="S" className="px-2 " onClick={() => formExcel()}>
                            엑셀 양식 다운로드
                        </Button>
                        <p className="text-warning text-sm">엑셀 업로드시, 미확인 및 본점으로 신규 등록됩니다.</p>
                    </div>
                    <input type="file" accept=".xls,.xlsx" ref={fileInputRef} className="hidden" onChange={handleFileChange} />

                    <div
                        className="cursor-pointer flex flex-col rounded-[5px] gap-5 mx-6 my-5 items-center justify-center border border-dashed border-gray4 h-40"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={() => {
                            fileInputRef.current?.click();
                        }}
                    >
                        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFile} />

                        {excelFile ? (
                            <>
                                <img src={excelImage} alt="excelImage" />
                                <p className="text-primary2 underline">{excelFile.name}</p>
                            </>
                        ) : (
                            <>
                                <img src={excelImage} alt="excelImage" />
                                <p className="text-gray3">엑셀 파일을 드래그해서 업로드하세요.</p>
                            </>
                        )}
                    </div>

                    {errorMessage && <p className="text-error mt-2 ml-5">{errorMessage}</p>}

                    <div className="px-5 pb-5 flex flex-col gap-2">
                        <p className="text-lg">
                            {uploadStatus === "uploading"
                                ? "업로드 중입니다."
                                : uploadStatus === "success"
                                  ? "업로드 완료"
                                  : "파일을 업로드해주세요."}
                        </p>
                        <div className="flex py-5 gap-5 rounded-xl bg-primary4 items-center">
                            <div className="flex flex-col items-center justify-center gap-3 w-full">
                                <p>전체내역</p>
                                <p className="font-semibold">{result?.all_cnt}</p>
                            </div>
                            <div className="bg-gray4 h-[55px] w-1" />
                            <div className="flex flex-col items-center justify-center gap-3 w-full">
                                <p>업로드 성공</p>
                                <p className="font-semibold text-skyblue">{result?.success_cnt}</p>
                            </div>
                            <div className="bg-gray4 h-[55px] w-1" />
                            <div className="flex flex-col items-center justify-center gap-3 w-full">
                                <p>업로드 실패</p>
                                <button onClick={() => setFailPop(true)} className="font-semibold text-error underline">
                                    {result?.err_cnt}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {failPop && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center overflow-hidden justify-center z-50">
                    <div className="w-[480px] h-[500px] bg-white rounded-2xl border shadow-lg overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-5">
                            <p className="text-[#1C1C1E] text-2xl font-bold">업로드 실패</p>
                            <CloseIcon className="cursor-pointer" onClick={() => setFailPop(false)} />
                        </div>
                        <div className="px-6 overflow-y-auto">
                            <table className=" w-full h-full text-sm border-t border-[#999999] text-gray2">
                                <thead className="h-10 border-b border-[#DDDDDD]">
                                    <tr className="">
                                        <th className="min-w-[60px]">라인</th>
                                        <th className="w-1/3">항목명</th>
                                        <th className="w-1/3">입력값</th>
                                        <th className="w-1/3">에러메시지</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result?.err_line.length || -1 > 0 ? (
                                        result?.err_line.map((item, index) => (
                                            <tr key={index} className="text-center h-12 border-b border-[#DDDDDD]">
                                                <td className="">{item.line}</td>
                                                <td className="">{item.name}</td>
                                                <td className="">{item.value}</td>
                                                <td className="">{item.errmsg}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="pt-40 text-center text-gray-500">
                                                실패 내역이 없습니다.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Excel;
